import { navStateStore } from "../../state/nav-state-store";
import { NaviError, NaviErrorTypes } from "../../utility/navi-error";
import { IAbTestingService } from "./ab-testing-service.interface";
import {
  AvailableExperiment,
  Experiment,
  ExperimentDefinition,
  experimentsDefinitions,
  MatomoClient,
  Variation,
} from "../../utility/constants/ab-testing";
import Logger from "../logger";
import featureToggles from "../feature-toggles";
import {
  ABVariation,
  Account,
  TLink,
  TMenu,
  VariationMenuItem,
} from "../../entities/content";
import { navContentStore } from "../../state/nav-content-store";
import { getHTMLElement } from "../../utility/helper";
import { Application } from "../../utility/constants/app";

export class MatomoAbTestingService implements IAbTestingService {
  client: MatomoClient | null = null;

  currentVariationForExperiment: { [key: string]: Variation | null } = {};

  experiments: Experiment[] = [];

  async init(app: string): Promise<void> {
    if (this.shouldClientBeActive(app)) {
      try {
        await this.setClient();
        this.loadExperiments();
        this.loadContentGeneratorExperiments();
      } catch (e) {
        Logger.error("Could not initialize AbTesting", e as Error);
      }
    }
  }

  execute(experimentName: AvailableExperiment, app: string): void {
    if (this.shouldClientBeActive(app)) {
      try {
        const experiment = this.getExperiment(experimentName);
        const variation = this.getVariation(experiment);
        variation.activate();
        this.currentVariationForExperiment[experiment.name] = variation;
      } catch (e) {
        Logger.error(
          // eslint-disable-next-line
          `Could not execute experiment ${experimentName}`,
          e as Error,
        );
      }
    }
  }

  executeContentChange(menu: TMenu, app: string): TMenu[] {
    if (this.shouldClientBeActive(app)) {
      try {
        let variation =
          this.currentVariationForExperiment[menu.experienceName as string];
        if (!variation) {
          const experiment = this.getExperiment(menu.experienceName);
          const randomVariationName = experiment.selectRandomVariation();
          variation = experiment.variations.find(
            (v) => v.name === randomVariationName,
          )!;
          this.currentVariationForExperiment[experiment.name] = variation;
        }

        if (variation.name === "original") {
          return menu.children;
        }
        const variationMenuItems = variation.activate() as VariationMenuItem[];
        const variationMenuItem = variationMenuItems.find(
          (newVariationMenuItem) => newVariationMenuItem.section === menu.id,
        );
        if (variationMenuItem) {
          return variationMenuItem.children as TMenu[];
        }
        return menu.children;
      } catch (e) {
        Logger.error(
          // eslint-disable-next-line
          `Could not execute experiment ${menu.experienceName}`,
          e as Error,
        );
      }
    }
    return menu.children;
  }

  executeContentChangeAccount(account: Account, app: string): TLink[] {
    if (this.shouldClientBeActive(app)) {
      try {
        let variation =
          this.currentVariationForExperiment[account.experienceName as string];
        if (!variation) {
          const experiment = this.getExperiment(account.experienceName);
          const randomVariationName = experiment.selectRandomVariation();
          variation = experiment.variations.find(
            (v) => v.name === randomVariationName,
          )!;
          this.currentVariationForExperiment[experiment.name] = variation;
        }
        if (variation.name === "original") {
          return account.links;
        }
        const variationMenuItems = variation.activate() as VariationMenuItem[];
        const variationMenuItem = variationMenuItems.find(
          (newVariationMenuItem) =>
            newVariationMenuItem.section === "mainmenu.account",
        );
        if (variationMenuItem) {
          return variationMenuItem.children as TLink[];
        }
        return account.links;
      } catch (e) {
        Logger.error(
          // eslint-disable-next-line
          `Could not execute account experiment ${account.experienceName}`,
          e as Error,
        );
      }
    }
    return account.links;
  }

  private async setClient(): Promise<MatomoClient> {
    if (this.client) return Promise.resolve(this.client);

    await this.injectScript();
    const w = window as any;
    this.client = w.Matomo as MatomoClient;
    return this.client;
  }

  private loadExperiments(): void {
    const client = this.getClient();
    const ExperimentCreator = client.AbTesting.Experiment;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const experimentsDefinitions = this.getAbTestingDefinitions();
    // eslint-disable-next-line
    for (const experimentKey in experimentsDefinitions) {
      if (
        Object.prototype.hasOwnProperty.call(
          experimentsDefinitions,
          experimentKey,
        )
      ) {
        const experimentDefinition = experimentsDefinitions[experimentKey];
        const experiment = new ExperimentCreator(experimentDefinition);
        this.experiments.push(experiment);
        const randomVariationName = experiment.selectRandomVariation();
        const variation = experiment.variations.find(
          (v) => v.name === randomVariationName,
        )!;
        this.currentVariationForExperiment[experiment.name] = variation;
      }
    }
  }

  loadContentGeneratorExperiments(): void {
    if (featureToggles.isABTestingToggleActive()) {
      try {
        const client = this.getClient();
        const ExperimentCreator = client.AbTesting.Experiment;
        const contentDefinitions = navContentStore.state.abExperiences;
        if (contentDefinitions) {
          contentDefinitions.forEach((experimentDefinition) => {
            const variationDefinition: Variation[] =
              experimentDefinition.variations.map((variation: ABVariation) => {
                return {
                  name: variation.name,
                  activate: () => variation.variationMenuItems,
                };
              });
            variationDefinition.push({
              name: "original",
              activate: () => null,
            });
            const experiment = new ExperimentCreator({
              ...experimentDefinition,
              variations: variationDefinition,
            });
            this.experiments.push(experiment);
            const randomVariationName = experiment.selectRandomVariation();
            const variation = experiment.variations.find(
              (v) => v.name === randomVariationName,
            )!;
            this.currentVariationForExperiment[experiment.name] = variation;
          });
        }
      } catch (e) {
        Logger.error(
          // eslint-disable-next-line
          `Could not execute loadContentGeneratorExperiments`,
          e as Error,
        );
      }
    }
  }

  private getExperiment(name: AvailableExperiment): Experiment {
    const experiment = this.experiments.find((exp) => exp.name === name);
    if (!experiment)
      throw new NaviError(
        // eslint-disable-next-line
        `No experiment with name: ${name} found`,
        NaviErrorTypes.GENERAL,
      );
    return experiment;
  }

  private getVariation(experiment: Experiment): Variation {
    const variationName = experiment.getActivatedVariationName();
    const variation = experiment.variations.find(
      (variationToFind) => variationToFind.name === variationName,
    );

    if (!variation)
      throw new NaviError(
        `No variation in experiment: ${experiment.name} with name: ${variationName} found`,
        NaviErrorTypes.GENERAL,
      );
    return variation;
  }

  private getClient(): MatomoClient {
    if (!this.client)
      throw new NaviError(
        "No client is set, you must call init first",
        NaviErrorTypes.GENERAL,
      );
    return this.client;
  }

  private async injectScript(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.text = this.getMatomoScript();
      document.head.appendChild(s);

      const matomoScript = getHTMLElement(
        document.querySelector(
          'script[src="//cdn.matomo.cloud/porsche.matomo.cloud/matomo.js"]',
        ),
      );

      if (matomoScript) {
        matomoScript.onload = () => {
          resolve(true);
        };
        matomoScript.onerror = () =>
          reject(
            new NaviError(
              "Could not inject Matomo script",
              NaviErrorTypes.GENERAL,
            ),
          );
      } else {
        reject(
          new NaviError(
            "There is no HTML Script element available",
            NaviErrorTypes.GENERAL,
          ),
        );
      }
    });
  }

  private getAbTestingDefinitions(): ExperimentDefinition {
    return experimentsDefinitions;
  }

  private getMatomoScript(): string {
    const currentEnvironment = navStateStore.state.env;
    return `var _paq = window._paq = window._paq || [];
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);
    (function() {
      var u="https://porsche.matomo.cloud/";
      _paq.push(['setTrackerUrl', u+'matomo.php']);
      _paq.push(['setSiteId', ${currentEnvironment === "production" ? "2" : "1"}]);
      var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
      g.async=true; g.src='//cdn.matomo.cloud/porsche.matomo.cloud/matomo.js'; s.parentNode.insertBefore(g,s);
    })
   ();`;
  }

  private shouldClientBeActive(app: string): boolean {
    return (
      featureToggles.isABTestingToggleActive() &&
      app === Application.pcom &&
      navContentStore.state.abExperiences !== undefined
    );
  }

  getActivatedVariationName(experienceName: string): string | undefined {
    try {
      const experiment = this.getExperiment(experienceName);
      return experiment?.getActivatedVariationName();
    } catch {
      return undefined;
    }
  }
}

export const MatomoAbTesting = new MatomoAbTestingService();
